import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useGetMyTicketQuery,
  useReplyToMyTicketMutation,
  useReopenMyTicketMutation,
  buildTicketReplyFormData,
} from '../../features/tickets/ticketsApi';
import { TICKET_CATEGORIES, ticketReplySchema } from '../../lib/validators/ticketSchema';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import TicketStatusBadge from '../../components/tickets/TicketStatusBadge';
import TicketThread from '../../components/tickets/TicketThread';
import TicketAttachmentsInput from '../../components/tickets/TicketAttachmentsInput';
import { SkeletonDetailCard } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';

const CATEGORY_LABEL = Object.fromEntries(TICKET_CATEGORIES.map((c) => [c.value, c.label]));

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * GET /investor/support-tickets/{uuid} — own only. Reply is disabled with
 * an explanatory note once closed (409 either way, but the UI hides the
 * dead end rather than letting the investor discover it by trying).
 * Reopen only shows up while resolved — closed tickets can't be reopened
 * by anyone, open tickets have nothing to reopen.
 */
export default function TicketDetail() {
  const { id } = useParams();
  const toast = useToast();
  const { data: ticket, isLoading, isError, error } = useGetMyTicketQuery(id, { skip: !id });
  const [replyToTicket, { isLoading: isReplying }] = useReplyToMyTicketMutation();
  const [reopenTicket, { isLoading: isReopening }] = useReopenMyTicketMutation();

  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [replyError, setReplyError] = useState(null);

  const isClosed = ticket?.status === 'closed';
  const isResolved = ticket?.status === 'resolved';

  const onReply = async (e) => {
    e.preventDefault();
    setReplyError(null);

    const parsed = ticketReplySchema.safeParse({ body, attachments });
    if (!parsed.success) {
      setReplyError(parsed.error.issues[0]?.message || 'Please check your reply.');
      return;
    }

    const wasResolved = isResolved;
    try {
      const formData = buildTicketReplyFormData({ body, attachments });
      await replyToTicket({ id, formData }).unwrap();
      setBody('');
      setAttachments([]);
      toast.push({
        tone: 'success',
        message: wasResolved ? 'Reply sent — your ticket has been reopened.' : 'Reply sent.',
      });
    } catch (err) {
      if (err?.status === 409) {
        setReplyError('This ticket is closed and can no longer receive replies.');
        return;
      }
      setReplyError(authErrorMessage(err, { fallback: 'Could not send this reply.' }));
    }
  };

  const onReopen = async () => {
    try {
      await reopenTicket(id).unwrap();
      toast.push({ tone: 'success', message: 'Ticket reopened.' });
    } catch (err) {
      toast.push({ tone: 'danger', message: authErrorMessage(err, { fallback: 'Could not reopen this ticket.' }) });
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/tickets" className="text-sm text-accent hover:underline">
        ← Back to support
      </Link>

      {isLoading && <SkeletonDetailCard fields={4} />}

      {isError && (
        <Card>
          <p className="py-6 text-center text-danger">{authErrorMessage(error, { notFound: 'Ticket not found.' })}</p>
        </Card>
      )}

      {ticket && (
        <>
          <Card className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold text-foreground">{ticket.subject}</h1>
                <p className="text-sm text-foreground-muted mt-0.5">
                  {CATEGORY_LABEL[ticket.category] || ticket.category} · Opened {formatDate(ticket.createdAt)}
                </p>
              </div>
              <TicketStatusBadge status={ticket.status} />
            </div>

            {isResolved && (
              <div className="rounded-xl border border-warning/30 bg-warning-soft p-3 text-sm text-warning flex flex-wrap items-center justify-between gap-3">
                <span>This ticket was marked resolved{ticket.resolvedAt ? ` on ${formatDate(ticket.resolvedAt)}` : ''}.</span>
                <Button size="sm" variant="secondary" onClick={onReopen} isLoading={isReopening}>
                  Reopen
                </Button>
              </div>
            )}

            {isClosed && (
              <div className="rounded-xl border border-border bg-background-elevated p-3 text-sm text-foreground-muted">
                This ticket is closed{ticket.closedAt ? ` (${formatDate(ticket.closedAt)})` : ''} and can no longer be replied to or reopened.
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Conversation" />
            <TicketThread messages={ticket.messages} />
          </Card>

          {!isClosed && (
            <Card>
              <CardHeader title="Reply" />
              <form onSubmit={onReply} className="space-y-4">
                <textarea
                  rows={4}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write your reply…"
                  className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
                />
                <TicketAttachmentsInput value={attachments} onChange={setAttachments} />
                {replyError && <p className="text-sm text-danger">{replyError}</p>}
                <Button type="submit" isLoading={isReplying}>
                  Send reply
                </Button>
              </form>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
